import React, { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { EyeIcon, EyeOffIcon } from 'lucide-react';
import { SubpageHeader } from '../components/SubpageHeader';
import { DialCodeSelect } from '../components/DialCodeSelect';
import { useDialCode } from '../hooks/useDialCode';
const ease = [0.22, 1, 0.36, 1] as const;
const image = 'https://cdn.magicpatterns.com/patterns/generated-images/62f89c66-1bd7-406a-9822-7f1a0cd5f1a8.jpg';
type Method = 'passphrase' | 'code';
type Status = 'idle' | 'sending' | 'sent';
const inputClass = 'w-full bg-transparent pb-3 pt-1.5 text-[18px] placeholder:font-light placeholder:text-ink/50 focus:outline-none';
export function Login() {
  const [method, setMethod] = useState<Method>('passphrase');
  const [email, setEmail] = useState('');
  const [passphrase, setPassphrase] = useState('');
  const [show, setShow] = useState(false);
  const [dialCode, setDialCode] = useDialCode();
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Members’ entrance — Superluxury';
  }, []);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (method === 'passphrase') {
      if (!/^\S+@\S+\.\S+$/.test(email.trim())) return setError('Please enter the email held with your membership.');
      if (passphrase.length < 6) return setError('Please enter your passphrase.');
    } else if (phone.replace(/\D/g, '').length < 6) {
      return setError('Please enter the number held with your membership.');
    }
    setError('');
    setStatus('sending');
    window.setTimeout(() => setStatus('sent'), 1200);
  };
  return <>
      <SubpageHeader data-id="element-1517" data-mp="1pshzj7o0t318" />
      <main className="grid min-h-[calc(100vh-80px)] grid-cols-1 bg-paper text-ink lg:grid-cols-2" data-id="element-1518" data-mp="1ptt2545qc4qr">
        <div className="relative hidden overflow-hidden bg-ink lg:block" data-id="element-1519" data-mp="1kuouwj2d8h32">
          <motion.img src={image} alt="The entrance hall of a private members’ club at night, a brass lamp glowing over an open guest book" className="absolute inset-0 h-full w-full object-cover" initial={{
          opacity: 0,
          scale: 1.05
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 2,
          ease
        }} data-id="element-1520" data-mp="2ai2m8qt8l20l" />
        </div>

        <div className="flex items-center px-6 py-16 md:px-16 md:py-24" data-id="element-1521" data-mp="2tf3wor5k7ubx">
          <div className="mx-auto w-full max-w-[480px]" data-id="element-1522" data-mp="2xgfc63upyz6e">
            <p className="text-[14px] font-light text-ink/60" data-id="element-1523" data-mp="2xeujykvvnsbp">For members</p>
            <h1 className="mt-3 font-editorial text-[48px] leading-[52px] tracking-[-0.01em] md:text-[60px] md:leading-[62px]" data-id="element-1524" data-mp="328p9w622t3j8">
              Members’ entrance
            </h1>

            <AnimatePresence mode="wait" initial={false} data-id="element-1525" data-mp="3c7ehn609d7lr">
              {status === 'sent' ? <motion.div key="sent" initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.6,
              ease
            }} role="status" className="mt-10 border-t border-ink pt-8" data-id="element-1526" data-mp="3h5zk7c2zg3dd">
                  <p className="text-[22px] font-normal" data-id="element-1527" data-mp="qktx7nvo0j39">One moment more</p>
                  <p className="mt-3 text-[16px] font-light leading-[26px] text-ink/75" data-id="element-1528" data-mp="qmy9at3pwoem">
                    {method === 'passphrase' ? 'A confirmation has been sent to the device held with your membership. Approve it to enter.' : `A one-time code has been sent to ${dialCode} ${phone}. Open the link in the message to enter.`}
                  </p>
                  <button type="button" onClick={() => setStatus('idle')} className="mt-8 text-[15px] font-medium underline underline-offset-4" data-id="element-1529" data-mp="19hd92kxusl3i">
                    Use another method
                  </button>
                </motion.div> : <motion.form key="form" onSubmit={submit} noValidate initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.4,
              ease
            }} className="mt-10" aria-label="Member login" data-id="element-1530" data-mp="1rpq4dntdybz0">
                  <div role="tablist" aria-label="Sign-in method" className="flex gap-8 border-b border-line" data-id="element-1531" data-mp="2ag7vxp2hf1is">
                    {([['passphrase', 'Email & passphrase'], ['code', 'One-time code']] as const).map(([m, label]) => <button key={m} type="button" role="tab" aria-selected={method === m} onClick={() => {
                  setMethod(m);
                  setError('');
                }} className={`-mb-px border-b pb-3 text-[15px] transition-colors duration-200 ${method === m ? 'border-ink font-medium text-ink' : 'border-transparent font-light text-ink/55 hover:text-ink'}`} data-id="element-1532" data-mp="2of7hgnup70aq">
                        {label}
                      </button>)}
                  </div>

                  {method === 'passphrase' ? <div className="mt-8 space-y-8" data-id="element-1533" data-mp="7srg93iedkm5">
                      <div data-id="element-1534" data-mp="7uc7ok9f0nfs">
                        <label htmlFor="login-email" className="block text-[13px] font-light text-ink/75" data-id="element-1535" data-mp="3rfjme13vz4g">
                          Email
                        </label>
                        <div className="border-b border-ink" data-id="element-1536" data-mp="1j6d2afsyn63g">
                          <input id="login-email" type="email" autoComplete="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="The address held with us" className={inputClass} data-id="element-1537" data-mp="1f7cojbpcatnb" />
                        </div>
                      </div>
                      <div data-id="element-1538" data-mp="3pb3i18ulgk04">
                        <div className="flex items-baseline justify-between" data-id="element-1539" data-mp="3pbv2bniqqvb3">
                          <label htmlFor="login-pass" className="block text-[13px] font-light text-ink/75" data-id="element-1540" data-mp="3u8vac3z5i84u">
                            Passphrase
                          </label>
                          <Link to="/private-line" className="text-[13px] font-light text-ink/70 underline underline-offset-2 hover:text-ink" data-id="element-1541" data-mp="6wc5z74f6eqk">
                            Forgotten it?
                          </Link>
                        </div>
                        <div className="flex items-end border-b border-ink" data-id="element-1542" data-mp="3ak4ck6p22bkr">
                          <input id="login-pass" type={show ? 'text' : 'password'} autoComplete="current-password" value={passphrase} onChange={e => setPassphrase(e.target.value)} placeholder="Your passphrase" className={inputClass} data-id="element-1543" data-mp="3313gu7khxm7y" />
                          <button type="button" onClick={() => setShow(s => !s)} aria-label={show ? 'Hide passphrase' : 'Show passphrase'} className="pb-3.5 pl-3 text-ink/60 transition-colors duration-150 hover:text-ink" data-id="element-1544" data-mp="2fck9bu99tdgx">
                            {show ? <EyeOffIcon className="h-4 w-4" strokeWidth={1.5} data-id="element-1545" data-mp="21c11oo8pw9fa" /> : <EyeIcon className="h-4 w-4" strokeWidth={1.5} data-id="element-1546" data-mp="1wd6dt7vw7uob" />}
                          </button>
                        </div>
                      </div>
                    </div> : <div className="mt-8" data-id="element-1547" data-mp="3p8fovh804e5g">
                      <label htmlFor="login-phone" className="block text-[13px] font-light text-ink/75" data-id="element-1548" data-mp="3p74lvjmdrafe">
                        Phone held with your membership
                      </label>
                      <div className="flex items-end gap-3 border-b border-ink" data-id="element-1549" data-mp="3gawxzw7of5ln">
                        <DialCodeSelect id="login-code" value={dialCode} onChange={setDialCode} data-id="element-1550" data-mp="31id1vfiy1ydp" />
                        <span className="mb-3.5 h-5 w-px bg-ink/25" aria-hidden="true" data-id="element-1551" data-mp="36c7ey041nf3t" />
                        <input id="login-phone" type="tel" inputMode="tel" autoComplete="tel-national" value={phone} onChange={e => setPhone(e.target.value)} placeholder="Your number" className="min-w-0 flex-1 bg-transparent pb-3 pt-1.5 text-[18px] placeholder:font-light placeholder:text-ink/50 focus:outline-none" data-id="element-1552" data-mp="2skj2fvpvuomb" />
                      </div>
                    </div>}

                  {error && <p role="alert" className="mt-4 text-[14px] text-ink" data-id="element-1553" data-mp="28xpp6bm3xute">
                      {error}
                    </p>}

                  <button type="submit" disabled={status === 'sending'} className="mt-10 w-full bg-ink py-4 text-[17px] font-medium text-paper transition-opacity duration-150 hover:opacity-85 disabled:opacity-60" data-id="element-1554" data-mp="2mvmmja8dy594">
                    {status === 'sending' ? 'Verifying…' : method === 'passphrase' ? 'Enter' : 'Send my code'}
                  </button>
                </motion.form>}
            </AnimatePresence>

            <div className="mt-14 flex flex-col gap-3 border-t border-line pt-6 text-[15px] font-light text-ink/70 sm:flex-row sm:justify-between" data-id="element-1555" data-mp="31nog6cjv420p">
              <span data-id="element-1556" data-mp="kcnpzt9b8oj0">
                Not yet a member?{' '}
                <Link to="/membership" className="font-medium text-ink underline-offset-4 hover:underline" data-id="element-1557" data-mp="kab26zgvfjy1">
                  Request membership
                </Link>
              </span>
              <Link to="/private-line" className="font-medium text-ink underline-offset-4 hover:underline" data-id="element-1558" data-mp="ag19thrxilnb">
                Private Line →
              </Link>
            </div>
          </div>
        </div>
      </main>
    </>;
}